import { useEffect, useState, type ChangeEvent, type ReactNode } from 'react';
import type { FieldValues, Path, PathValue, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { apiFormData } from '@/lib/api';
import { customFieldMediaSrc, parseCustomFieldMediaIds } from '@/lib/custom-field-display';
import { chileanPesoCaret, digitsBeforeChileanPesoCaret, formatChileanPeso } from '@/lib/format-chilean-peso';
import { customFieldInputKind } from '@/lib/user-custom-field-types';

const FIELD =
  'w-full rounded-lg border border-dash-outline-variant bg-white px-4 py-3 text-base text-dash-on-surface outline-none transition-all focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20';

const LABEL = 'text-sm font-medium text-dash-on-surface-variant';

export interface UserCustomFieldOption {
  label: string;
  value: string;
  isActive?: boolean;
}

export interface UserCustomFieldDefinition {
  id: string;
  name: string;
  slug: string;
  type: string;
  options?: UserCustomFieldOption[];
  required?: boolean;
  readonly?: boolean;
  placeholder?: string | null;
  helpText?: string | null;
  maxFiles?: number;
  optionsLayout?: 'vertical' | 'horizontal';
  formWidth?: 'half' | 'full' | null;
  sortOrder?: number;
  groupName?: string;
}

export type CustomFieldFormValues = Record<string, string | string[] | boolean>;

interface UserCustomFieldInputProps<T extends FieldValues> {
  field: UserCustomFieldDefinition;
  register: UseFormRegister<T>;
  setValue: UseFormSetValue<T>;
  watch: UseFormWatch<T>;
  className?: string;
}

function activeOptions(field: UserCustomFieldDefinition): UserCustomFieldOption[] {
  return (field.options ?? []).filter((option) => option.isActive !== false && (option.label || option.value));
}

export function UserCustomFieldInput<T extends FieldValues>({
  field,
  register,
  setValue,
  watch,
  className,
}: UserCustomFieldInputProps<T>) {
  const name = `customFields.${field.slug}` as Path<T>;
  const kind = customFieldInputKind(field.type);
  const label = (
    <label htmlFor={`cf-${field.slug}`} className={LABEL}>
      {field.name}
      {field.required ? <span className="text-error-600"> *</span> : null}
    </label>
  );
  const hint = field.helpText ? <p className="text-xs text-dash-on-surface-variant">{field.helpText}</p> : null;
  const options = activeOptions(field);
  const registerOpts = { required: Boolean(field.required) };
  const locked = Boolean(field.readonly);
  const placeholder = field.placeholder ?? '';

  if (kind === 'textarea') {
    return (
      <div className={`space-y-2 ${className ?? ''}`}>
        {label}
        <textarea
          id={`cf-${field.slug}`}
          rows={3}
          placeholder={placeholder}
          readOnly={locked}
          className={`${FIELD} resize-none ${locked ? 'bg-secondary-50' : ''}`}
          {...register(name, registerOpts)}
        />
        {hint}
      </div>
    );
  }

  if (kind === 'checkbox') {
    return (
      <div className={`space-y-2 ${className ?? ''}`}>
        <div className={`flex items-center gap-3 ${locked ? 'pointer-events-none opacity-70' : ''}`}>
          <input
            id={`cf-${field.slug}`}
            type="checkbox"
            className="h-4 w-4 rounded border-dash-outline-variant text-dash-primary focus:ring-dash-secondary/20"
            {...register(name, registerOpts)}
          />
          <label htmlFor={`cf-${field.slug}`} className="text-sm font-medium text-dash-on-surface">
            {field.name}
            {field.required ? <span className="text-error-600"> *</span> : null}
          </label>
        </div>
        {hint}
      </div>
    );
  }

  if (kind === 'switch') {
    return (
      <div className={`space-y-2 ${className ?? ''}`}>
        <label className={`flex cursor-pointer items-center gap-3 ${locked ? 'pointer-events-none opacity-70' : ''}`}>
          <span className="relative inline-block h-6 w-11 shrink-0">
            <input id={`cf-${field.slug}`} type="checkbox" className="peer sr-only" {...register(name, registerOpts)} />
            <span
              className="block h-6 w-11 rounded-full bg-secondary-200 transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-dash-secondary/20 peer-checked:bg-dash-primary"
              aria-hidden="true"
            />
            <span className="pointer-events-none absolute left-0.5 top-0.5 block h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5" aria-hidden="true" />
          </span>
          <span className="text-sm font-medium text-dash-on-surface">
            {field.name}
            {field.required ? <span className="text-error-600"> *</span> : null}
          </span>
        </label>
        {hint}
      </div>
    );
  }

  if (kind === 'select') {
    return (
      <div className={`space-y-2 ${className ?? ''}`}>
        {label}
        <div className={`relative ${locked ? 'pointer-events-none' : ''}`}>
          <select
            id={`cf-${field.slug}`}
            className={`${FIELD} cursor-pointer appearance-none pr-10 ${locked ? 'bg-secondary-50' : ''}`}
            {...register(name, registerOpts)}
          >
            <option value="">Seleccionar...</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label || option.value}
              </option>
            ))}
          </select>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-dash-on-surface-variant" aria-hidden="true">
            <span className="material-symbols-outlined">expand_more</span>
          </span>
        </div>
        {hint}
      </div>
    );
  }

  if (kind === 'radio' || kind === 'multiselect') {
    const sideBySide = kind === 'multiselect' && field.optionsLayout === 'horizontal';
    return (
      <div className={`space-y-2 ${className ?? ''}`}>
        <span className={LABEL}>
          {field.name}
          {field.required ? <span className="text-error-600"> *</span> : null}
        </span>
        <div className={`${sideBySide ? 'flex flex-wrap gap-x-6 gap-y-3' : 'space-y-2'} rounded-lg border border-dash-outline-variant bg-white p-3 ${locked ? 'pointer-events-none opacity-70' : ''}`}>
          {options.length === 0 ? (
            <p className="text-sm text-dash-on-surface-variant">Sin opciones configuradas.</p>
          ) : (
            options.map((option) => (
              <label key={option.value} className={`${sideBySide ? 'inline-flex whitespace-nowrap' : 'flex'} items-center gap-2 text-sm text-dash-on-surface`}>
                <input
                  type={kind === 'radio' ? 'radio' : 'checkbox'}
                  value={option.value}
                  className="border-dash-outline-variant text-dash-primary focus:ring-dash-secondary/20"
                  {...register(name, registerOpts)}
                />
                {option.label || option.value}
              </label>
            ))
          )}
        </div>
        {hint}
      </div>
    );
  }

  if (kind === 'peso') {
    return (
      <ChileanPesoInput
        field={field}
        name={name}
        label={label}
        hint={hint}
        locked={locked}
        required={Boolean(field.required)}
        placeholder={placeholder}
        className={className}
        register={register}
        watch={watch}
        setValue={setValue}
      />
    );
  }

  if (kind === 'file' || kind === 'image') {
    return (
      <CustomFieldFileInput
        field={field}
        kind={kind}
        name={name}
        label={label}
        hint={hint}
        locked={locked}
        required={Boolean(field.required)}
        className={className}
        register={register}
        watch={watch}
        setValue={setValue}
      />
    );
  }

  const inputType =
    kind === 'number' || kind === 'decimal'
      ? 'number'
      : kind === 'email'
        ? 'email'
        : kind === 'url'
          ? 'url'
          : kind === 'phone'
            ? 'tel'
            : kind === 'date'
              ? 'date'
              : kind === 'datetime'
                ? 'datetime-local'
                : kind === 'color'
                  ? 'color'
                  : 'text';

  return (
    <div className={`space-y-2 ${className ?? ''}`}>
      {label}
      <input
        id={`cf-${field.slug}`}
        type={inputType}
        step={kind === 'decimal' ? '0.01' : kind === 'number' ? '1' : undefined}
        placeholder={placeholder}
        readOnly={locked}
        className={`${FIELD} ${locked ? 'bg-secondary-50' : ''}`}
        {...register(name, registerOpts)}
      />
      {hint}
    </div>
  );
}

function ChileanPesoInput<T extends FieldValues>({
  field,
  name,
  label,
  hint,
  locked,
  required,
  placeholder,
  className,
  register,
  watch,
  setValue,
}: {
  field: UserCustomFieldDefinition;
  name: Path<T>;
  label: ReactNode;
  hint: ReactNode;
  locked: boolean;
  required: boolean;
  placeholder: string;
  className?: string;
  register: UseFormRegister<T>;
  watch: UseFormWatch<T>;
  setValue: UseFormSetValue<T>;
}) {
  const registration = register(name, { required });
  const watched = watch(name);
  const formatted = formatChileanPeso(typeof watched === 'string' ? watched : '');

  useEffect(() => {
    if (typeof watched !== 'string' || watched === formatted) return;
    setValue(name, formatted as PathValue<T, Path<T>>, { shouldDirty: false });
  }, [formatted, name, setValue, watched]);

  return (
    <div className={`space-y-2 ${className ?? ''}`}>
      {label}
      <input
        id={`cf-${field.slug}`}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder={placeholder || '1.500.000'}
        readOnly={locked}
        className={`${FIELD} ${locked ? 'bg-secondary-50' : ''}`}
        {...registration}
        value={formatted}
        onChange={(event) => {
          const input = event.currentTarget;
          const digitsBefore = digitsBeforeChileanPesoCaret(input.value, input.selectionStart ?? input.value.length);
          const next = formatChileanPeso(input.value);
          setValue(name, next as PathValue<T, Path<T>>, { shouldDirty: true, shouldValidate: true });
          requestAnimationFrame(() => {
            const pos = chileanPesoCaret(next, digitsBefore);
            input.setSelectionRange(pos, pos);
          });
        }}
      />
      {hint}
    </div>
  );
}

function CustomFieldFileInput<T extends FieldValues>({
  field,
  kind,
  name,
  label,
  hint,
  locked,
  required,
  className,
  register,
  watch,
  setValue,
}: {
  field: UserCustomFieldDefinition;
  kind: 'file' | 'image';
  name: Path<T>;
  label: ReactNode;
  hint: ReactNode;
  locked: boolean;
  required: boolean;
  className?: string;
  register: UseFormRegister<T>;
  watch: UseFormWatch<T>;
  setValue: UseFormSetValue<T>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const maxFiles = Math.max(1, field.maxFiles ?? 1);
  const ids = parseCustomFieldMediaIds(watch(name));
  const remaining = Math.max(0, maxFiles - ids.length);

  function persist(next: string[]) {
    const value = maxFiles <= 1 ? (next[0] ?? '') : JSON.stringify(next);
    setValue(name, value as PathValue<T, Path<T>>, { shouldDirty: true, shouldValidate: true });
  }

  async function onFileChange(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (files.length === 0 || remaining <= 0) return;
    setBusy(true);
    setError(null);
    try {
      const uploaded: string[] = [];
      for (const file of files.slice(0, remaining)) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('title', file.name);
        const result = await apiFormData<{ id: string }>('/media/upload', formData);
        uploaded.push(result.id);
      }
      persist([...ids, ...uploaded]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo subir el archivo');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`space-y-2 ${className ?? ''}`}>
      {label}
      <input type="hidden" {...register(name, { required })} />
      {ids.length > 0 ? (
        <ul className="flex flex-wrap gap-3">
          {ids.map((id) => {
            const mediaSrc = customFieldMediaSrc(id);
            return (
              <li key={id} className="flex flex-col items-start gap-1">
                {kind === 'image' && mediaSrc ? (
                  <img src={mediaSrc} alt="" className="h-24 w-24 rounded-lg border border-dash-outline-variant object-cover" />
                ) : (
                  <a href={mediaSrc ?? id} className="text-sm font-medium text-dash-secondary hover:underline" target="_blank" rel="noopener noreferrer">
                    Archivo
                  </a>
                )}
                {locked ? null : (
                  <button
                    type="button"
                    className="text-sm font-medium text-error-600 hover:underline"
                    onClick={() => persist(ids.filter((item) => item !== id))}
                  >
                    Quitar
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
      {remaining > 0 ? (
        <label className={`flex cursor-pointer flex-col items-center rounded-xl border-2 border-dashed border-dash-outline-variant bg-secondary-50 p-6 text-center transition-colors hover:border-dash-primary ${locked ? 'pointer-events-none opacity-70' : ''}`}>
          <input
            id={`cf-${field.slug}`}
            type="file"
            accept={kind === 'image' ? 'image/*' : undefined}
            multiple={remaining > 1}
            className="sr-only"
            disabled={locked || busy}
            onChange={(event) => void onFileChange(event)}
          />
          <span className="material-symbols-outlined mb-2 text-3xl text-dash-primary" aria-hidden="true">
            {kind === 'image' ? 'add_photo_alternate' : 'attach_file'}
          </span>
          <p className="text-sm font-medium text-dash-on-surface">
            {busy
              ? 'Subiendo…'
              : kind === 'image'
                ? remaining > 1
                  ? `Haga clic para subir imágenes (hasta ${remaining})`
                  : 'Haga clic para subir una imagen'
                : remaining > 1
                  ? `Haga clic para subir archivos (hasta ${remaining})`
                  : 'Haga clic para subir un archivo'}
          </p>
          {maxFiles > 1 ? (
            <p className="mt-1 text-xs text-dash-on-surface-variant">
              {ids.length} de {maxFiles} archivos
            </p>
          ) : null}
        </label>
      ) : null}
      {error ? (
        <p className="text-sm text-error-600" role="alert">
          {error}
        </p>
      ) : null}
      {hint}
    </div>
  );
}
